import { Typography, Button, Box, RadioGroup, FormControlLabel, Tooltip, FormControl, Radio, Checkbox, useTheme, TextField, CircularProgress } from '@mui/material'
import React, { useState } from 'react'
import { toast } from 'react-toastify'
import ToggleOnIcon from '@mui/icons-material/ToggleOn'
import ToggleOffIcon from '@mui/icons-material/ToggleOff'
import { useEffect } from 'react'
// import AudioPlayer from '../common/AudioRecorder'
import { AudioFileOutlined, CheckCircleOutline } from '@mui/icons-material'
import { useUserStore } from '../../store/user.store'
import api from '../../service/api'
import { latexDelimiters, shuffleArray } from '../../utils/utilities'
import Latex from 'react-latex-next'
import { observer } from 'mobx-react'
import { useAdmissionExamStore } from '../../store/admission-exam.store'
import CustomChip from '../common/CustomChip'
import { useTranslation } from 'react-i18next'

const AnswerSection = observer( ( { allAnswered, saving, submitAnswer, submissions, handleClickOnFinishExam, toggleQuestions, showQuestions } ) => {
    const [currentSubmissions, setcurrentSubmissions] = useState( null )
    const [resetting, setResetting] = useState( false )

    const { palette } = useTheme()
    const { t } = useTranslation()

    const ExamStore = useAdmissionExamStore()
    const UserStore = useUserStore()

    const currentQuestionIndex = ExamStore.getAttemptDetails.currentQuestionIndex
    const { question, options: questionOptions } = ExamStore.getAttemptDetails.currentQuestion
    const { translationsAvailable, selectedLanguage = "", defaultLanguage } = ExamStore.getTranslationDetails || {}


    const displayQuestion = question?.translations?.[selectedLanguage] || question.question_text
    const translationAvailableForSelectedLanguage = defaultLanguage === selectedLanguage ? true : Boolean( question?.translations?.[selectedLanguage] )


    const moveToNextQuestion = () => {
        ExamStore.moveToNextQuestion()
    }

    const handleSingleSelectionChange = async e => {
        const newSubmissionValue = { ...currentSubmissions, [question.question_id]: e.target.value }
        setcurrentSubmissions( newSubmissionValue )
        await submitAnswer( newSubmissionValue[question.question_id], question.question_type, question.question_answer_selection )
        moveToNextQuestion()
    }

    const handleMultipleSelectionChange = ( e, option ) => {
        let newSubmissionValue
        if ( currentSubmissions[question.question_id] )
            newSubmissionValue = { ...currentSubmissions, [question.question_id]: { ...currentSubmissions[question.question_id], [option.answer_id]: e.target.checked ? 1 : 0 } }
        else
            newSubmissionValue = { ...currentSubmissions, [question.question_id]: { [option.answer_id]: e.target.checked ? 1 : 0 } }
        setcurrentSubmissions( newSubmissionValue )
        submitAnswer( newSubmissionValue[question.question_id], question.question_type, question.question_answer_selection )
    }

    const resetAnswer = () => {
        setResetting( true )
        const newSubmissionValue = { ...currentSubmissions, [question.question_id]: '-1' }
        setcurrentSubmissions( newSubmissionValue )
        submitAnswer( newSubmissionValue[question.question_id], question.question_type, question.question_answer_selection )
        setResetting( false )
    }

    useEffect( () => {
        const getAudioUrls = async () => {
            let signedUrlsList = []
            try {
                for ( let i = 0; i < question?.audio_attachments_keys.length; i++ ) {
                    const key = question.audio_attachments_keys[i]
                    const { data: signedUrl } = await api.getAudioPresignedUrl( ExamStore.getExam.exam.exam_id, question.question_id, { key } )
                    signedUrlsList.push( signedUrl.data )
                }
                // setSignedUrls( signedUrlsList )
            } catch ( err ) {
                if ( err?.response?.status === 401 || err?.response?.data?.unauth ) {
                    UserStore.setSessionState( "ended" )
                }
                else
                    toast( err?.response?.data?.message || "Something went wrong while fetchiing audio attachments", { containerId: "main" } )
            }
        }
        getAudioUrls()
        // }, [question] )
    }, [question, ExamStore, UserStore] )


    useEffect( () => {
        const subs = {}
        submissions.forEach( sub => {
            if ( sub.answer_id )
                if ( sub.question_answer_selection === 'single' )
                    subs[sub.question_id] = sub.answer_id
                else
                    subs[sub.question_id] = subs[sub.question_id] ? { ...subs[sub.question_id], [sub.answer_id]: 1 } : { [sub.answer_id]: 1 }
            else
                subs[sub.question_id] = sub.answer_descriptive
        } )
        if ( ExamStore.getExam.exam.enable_randomize )
            shuffleArray( questionOptions )
        setcurrentSubmissions( subs )
    }, [submissions, ExamStore, question] )

    return (
        <Box borderRadius="0 0 10px 0" sx={{ height: "100%", overflow: "auto", flexGrow: 1, }} display="flex" flexDirection="column" >
            <Box flexGrow={1} overflow="auto" className="custom-scrollbar" alignItems="flex-start" bgcolor={palette.common.bg} display="flex" flexDirection="column" padding="20px" >
                <Tooltip title="Toggle questions">
                    <Button onClick={toggleQuestions} startIcon={showQuestions ? <ToggleOnIcon /> : <ToggleOffIcon />} sx={{ textTransform: "capitalize" }}>
                        {showQuestions ? t( "button.hideQuestions" ) : t( "button.showQuestions" )}
                    </Button>
                </Tooltip>
                {question && <Box sx={{ userSelect: "none" }} >

                    {translationsAvailable && !translationAvailableForSelectedLanguage && selectedLanguage?.toLowerCase() === "en" && <CustomChip value={t( "message.translationUnvailable" )} />}

                    <Typography fontSize={{ lg: '14px', md: "14px", sm: "12px", xs: "12px" }} paddingLeft="10px" variant='subtitle2' color="textSecondary">{t( "label.question" )} {currentQuestionIndex + 1}</Typography>
                    <Box marginBottom="20px" fontSize="16px" display="flex" alignItems="center" flexWrap="wrap" gap="10px" fontWeight="600" marginTop="10px" color={palette.common.font} paddingLeft="10px" align='justify'>

                        {displayQuestion.trim() && <Latex delimiters={latexDelimiters} >{displayQuestion.trim().replace( "LATEX-" )}</Latex>}
                        {submissions?.find( s => s.question_id === question.question_id ) ? <Typography variant='subtitle2' color="success.main" display="flex" alignItems="center"> <CheckCircleOutline sx={{ fontSize: "16px" }} /> {t( "label.answered" )}</Typography> : ""}

                    </Box>
                    {question.question_hint && <Typography paddingLeft="10px" fontSize="13px" fontStyle="italic" variant='body2' align='justify' color="textSecondary">HINT: {question.question_hint}</Typography>}
                    {question.audio_attachments.length > 0 && <Box display="flex" margin="10px 0 0 10px" flexWrap="wrap" gap="10px" alignItems="center">
                        {question.audio_attachments.map( ( attachment, index ) => (
                            <Box padding="5px" bgcolor="#fff" display="flex" gap="10px" alignItems="center" border="1px solid #d3d3d3">
                                <AudioFileOutlined sx={{ fontSize: "20px" }} />
                                {/* <AudioPlayer audioName={question.audio_attachments[index]} audioSrc={signedUrls[index]} /> */}
                            </Box>
                        ) )}
                    </Box>}
                </Box>}
                {currentSubmissions && question &&
                    <Box flexGrow={1} width="100%" className="custom-scrollbar">
                        <form method="post" id='answer-form' style={{ display: "flex", height: "100%", width: "100%", flexDirection: "column", justifyContent: "space-between" }}>
                            <Box style={{ flexGrow: 1, marginTop: "20px" }}>
                                <FormControl fullWidth>
                                    {question.question_type === 'MCQ' && question.question_answer_selection === 'single' &&
                                        <>
                                            <RadioGroup sx={{ padding: "0 0 0 20px", display: "flex", gap: "10px", flexDirection: "column", maxWidth: "600px", width: "90%" }} value={currentSubmissions[question.question_id] ? currentSubmissions[question.question_id] : 'NA'} name={String( question.question_id )} onChange={handleSingleSelectionChange}>
                                                {questionOptions.map( ( option, index ) => {

                                                    const optionText = option?.translations?.[ExamStore.getTranslationDetails.selectedLanguage] || option.answer_text

                                                    return (
                                                        <FormControlLabel disabled={saving} key={option.answer_id} value={option.answer_id} control={<Radio />}
                                                            label={<Latex delimiters={latexDelimiters} >{optionText.trim().replace( "LATEX-" )}</Latex>}
                                                            sx={( currentSubmissions[question.question_id] && parseInt( currentSubmissions[question.question_id] ) === parseInt( option.answer_id ) ) ? {
                                                                width: "90%",
                                                                maxWidth: "600px",
                                                                padding: "10px",
                                                                borderRadius: "5px",
                                                                background: palette.common.bg,
                                                                display: "flex",
                                                                alignItems: "center",
                                                                transition: "0.2s ease",
                                                                whiteSpace: "pre-wrap",
                                                                userSelect: "none",
                                                                '& >p': {
                                                                    margin: "0", display: "inline-block"
                                                                },
                                                                boxShadow: "0px 5px 5px -3px rgb(0 0 0 / 10%), 0px 4px 8px 1px rgb(0 0 0 / 10%), 0px 3px 14px 2px rgb(0 0 0 / 12%)",
                                                                border: `2px solid ${palette.primary.main}`,
                                                                color: "primary.main",
                                                                "&:hover": {
                                                                    boxShadow: "0px 5px 5px -3px rgb(0 0 0 / 10%), 0px 4px 8px 1px rgb(0 0 0 / 10%), 0px 3px 14px 2px rgb(0 0 0 / 12%)",
                                                                    border: `2px solid ${palette.primary.main}`
                                                                }, "& span": {
                                                                    fontWeight: "bolder",
                                                                }
                                                            } : {
                                                                userSelect: "none",
                                                                width: "90%",
                                                                maxWidth: "600px",
                                                                padding: "10px",
                                                                borderRadius: "5px",
                                                                background: palette.common.bg,
                                                                border: "2px solid #aaaaaa99",
                                                                display: "flex",
                                                                alignItems: "center",
                                                                transition: "0.2s ease",
                                                                whiteSpace: "pre-wrap",
                                                                "&:hover": { boxShadow: "0px 5px 5px -3px rgb(0 0 0 / 10%), 0px 4px 8px 1px rgb(0 0 0 / 10%), 0px 3px 14px 2px rgb(0 0 0 / 12%)", border: "2px solid #fff" },
                                                                '& >p': {
                                                                    margin: "0", display: "inline-block"
                                                                }
                                                            }
                                                            } />
                                                    )
                                                } )}
                                            </RadioGroup>
                                        </>
                                    }
                                    {question.question_type === 'MCQ' && question.question_answer_selection !== 'single' &&
                                        <Box sx={{ display: "flex", gap: "10px", flexDirection: "column", padding: "0 0 0 20px" }}>
                                            {questionOptions.map( ( option, index ) => {

                                                const optionText = option?.translations?.[ExamStore.getTranslationDetails.selectedLanguage] || option.answer_text

                                                return (
                                                    <FormControlLabel key={option.answer_id}
                                                        value={option.answer_id}
                                                        checked={currentSubmissions[question.question_id] ? currentSubmissions[question.question_id][option.answer_id] === 1 ? true : false : false}
                                                        control={<Checkbox onChange={e => handleMultipleSelectionChange( e, option )} />}
                                                        label={<Latex delimiters={latexDelimiters} >{optionText.trim().split( "LATEX-" )[1]}</Latex>}
                                                        sx={{
                                                            padding: "10px", borderRadius: "5px", background: palette.common.bg, border: "2px solid #aaaaaa99", display: "flex", alignItems: "center", transition: "0.2s ease", whiteSpace: "pre-wrap", "&:hover": { boxShadow: "0px 5px 5px -3px rgb(0 0 0 / 10%), 0px 4px 8px 1px rgb(0 0 0 / 10%), 0px 3px 14px 2px rgb(0 0 0 / 12%)", border: "2px solid #fff" }, '& >p': {
                                                                margin: "0", display: "inline-block"
                                                            }
                                                        }} style={( currentSubmissions[question.question_id] && parseInt( currentSubmissions[question.question_id] ) === parseInt( option.answer_id ) ) ? { boxShadow: "0px 5px 5px -3px rgb(0 0 0 / 10%), 0px 4px 8px 1px rgb(0 0 0 / 10%), 0px 3px 14px 2px rgb(0 0 0 / 12%)", border: "2px solid #374ba4", color: "#374ba4", "&:hover": { boxShadow: "0px 5px 5px -3px rgb(0 0 0 / 10%), 0px 4px 8px 1px rgb(0 0 0 / 10%), 0px 3px 14px 2px rgb(0 0 0 / 12%)", border: "2px solid #374ba4" }, "& span": { fontWeight: "bolder", } } : null}
                                                    />
                                                )
                                            } )}
                                        </Box>
                                    }
                                    {/* {question.question_type === 'descriptive' && <ReactQuill modules={modules} value={currentSubmissions[question.question_id] ? currentSubmissions[question.question_id] : 'Answer goes here...'} onChange={( e ) => setcurrentSubmissions( { ...currentSubmissions, [question.question_id]: e } )} />} */}
                                    {question.question_type === 'Descriptive' && (

                                        <Box
                                            height="350px"
                                            margin="20px"
                                            display="flex"
                                            width="90%"
                                            borderRadius="5px"
                                        >
                                            <TextField
                                                fullWidth
                                                multiline
                                                rows={16}
                                                variant="outlined"
                                                value={currentSubmissions[question.question_id] || ''}
                                                onChange={( e ) =>
                                                    setcurrentSubmissions( {
                                                        ...currentSubmissions,
                                                        [question.question_id]: e.target.value,
                                                    } )
                                                }
                                                InputProps={{
                                                    style: { minHeight: '400px', }, // Ensure the TextField takes full height
                                                }}
                                                placeholder='Write your answers here'
                                            />
                                        </Box>
                                    )}
                                </FormControl>
                            </Box>

                            <Box marginTop="60px" justifyContent="space-between" display="flex" flexWrap="wrap" gap="20px" >
                                {submissions?.find( s => s.question_id === question.question_id ) ? <Button disabled={resetting || saving} variant='outlined' startIcon={resetting && <CircularProgress size={14} />} disableElevation color='secondary' sx={{ textTransform: "capitalize", }} onClick={resetAnswer}>{resetting ? t( "button.clearing" ) : t( "button.clear" )}</Button> : <Box component="span"></Box>}
                                <Box justifyContent="space-between" display="flex" flexWrap="wrap" gap="10px">
                                    <Button disableElevation disabled={saving} size='small' startIcon={saving && <CircularProgress size={14} />} variant="contained" sx={{ textTransform: "capitalize" }} onClick={moveToNextQuestion} >{saving ? t( "button.saving" ) : t( "button.next" )}</Button>
                                    {allAnswered && <Button variant='contained' color='errorMessage' size="small" disableElevation onClick={handleClickOnFinishExam} disabled={saving} sx={{ textTransform: "capitalize" }}>{t( "button.submitAndFinish" )}</Button>}
                                </Box>
                            </Box>
                        </form>
                    </Box >}
            </Box>
        </Box >
    )
} )


export default AnswerSection